import React, { useEffect, useState } from 'react';

import { Web } from '@mui/icons-material';
import { CSVLink } from 'react-csv';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import DriverSearchTable, { driverSearchColumns, driverSearchColumnsSummary } from './DriverSearchTable';
import Excel from '../../../assets/excel.png';
import { Breadcrumbs, Button, EdgeReportingWindow, SubHeader, ReportLoader } from '../../../components';
import { newDateFormatted } from '../../../utils';
import ImageColorChanger from '../../../utils/imageColorChanger';
import getCSVData from '../csv';
import { userHasAccess, getList } from '../operations';
import DriverSearchForm from './DriverSearchForm';
import getEdgeReportingMenu from '../getEdgeReportingMenu';

import './styles.scoped.css';

const DriverSearch = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { menuItems } = getEdgeReportingMenu(t);
  const { user } = useSelector(state => state.admin);
  const [driverStatuses, setDriverStatuses] = useState([]);
  const [data, setData] = useState([]);
  const [form, setForm] = useState({ ReportFormat: 'Summary' });
  const [loadingResults, setLoadingResults] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [isLoadingStatuses, setIsLoadingStatuses] = useState(false);

  useEffect(() => {
    if (menuItems) {
      // eslint-disable-next-line no-undef
      if (!userHasAccess(user, menuItems.find(i => i.to === window.location.pathname)?.id)) navigate('/');
    }
  }, [menuItems, navigate, user]);

  useEffect(() => {
    setIsLoadingStatuses(true);
    getList('driver-status').then(res => {
      const statuses = res.map(({ REF_CODE, REF_DESCRIPTION }) => ({
        label: REF_DESCRIPTION,
        value: REF_CODE,
      }));
      setIsLoadingStatuses(false);
      setDriverStatuses(statuses);
    });
  }, []);

  const fileName = `edge-reporting-${t('edge_reporting.driver_search.filename')}-${newDateFormatted.toString()}.csv`;

  return (
    <>
      <SubHeader
        text="EDGE Reporting"
        buttons={[{ icon: <Web sx={{ color: '#fff', fontSize: 30 }} />, onPress: () => navigate('/dashboard') }]}
        popoverTitle={`Need help with your ${t('edge_reporting.driver_search.title')}?`}
      />
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '30px',
        }}>
        <Breadcrumbs
          leftTitle="Reporting Home"
          middleTitle={showResults && t('edge_reporting.driver_search.title')}
          midItemAction={() => setShowResults(false)}
          rightTitle={showResults ? `Viewing Report (${form.ReportFormat})` : t('edge_reporting.driver_search.title')}
          route="/edge-reporting"
        />
        {showResults && (
          <CSVLink
            filename={fileName}
            data={getCSVData(data, form.ReportFormat === 'Summary' ? driverSearchColumnsSummary : driverSearchColumns)}>
            <ImageColorChanger
              src={Excel}
              style={{ padding: '20px 30px 0 0 ', cursor: 'pointer' }}
              alt="export-to-excel"
            />
          </CSVLink>
        )}
      </div>
      {loadingResults && <ReportLoader didAbort={() => setLoadingResults(false)} />}
      {showResults ? (
        <>
          <DriverSearchTable data={data} loading={loadingResults} summaryOrDetail={form.ReportFormat} />
          <div className="buttons-row">
            <Button variant="primary" onClick={() => setShowResults(false)} style={{ marginRight: '20px', width: '100px' }} text="Back" />

            <Button
              style={{ width: '100px' }}
              onClick={() => navigate('/edge-reporting')}
              variant="outline"
              text="Cancel"
            />
          </div>
        </>
      ) : (
        <EdgeReportingWindow
          containerStyle={{
            border: '2px solid #343a40',
          }}
          windowContent={
            <DriverSearchForm
              form={form}
              setForm={setForm}
              setLoadingResults={setLoadingResults}
              setData={setData}
              setShowResults={setShowResults}
              driverStatuses={driverStatuses}
              isLoadingStatuses={isLoadingStatuses}
            />
          }
          windowTitle="Criteria"
          headerStyle={{ backgroundColor: '#343a40' }}
        />
      )}
    </>
  );
};

export default DriverSearch;
